import axios from "axios";

import { env } from "@musicfy/env.mjs";

export enum ModalFunction {
  DELETE_MODEL = "delete-model",
  INFERENCE = "process-inference",
  TRAINING = "process-training",
  UPLOAD_MODEL = "upload-model",
  YOUTUBE_TO_MP3 = "youtube-to-mp3",
  TEXT_TO_MUSIC = "text-to-music",
  TEXT_TO_SONG = "text-to-song",
  GENERATE_VIDEO = "generate-video",
  GENERATE_STEMS = "generate-stems",
}

export enum ModalApp {
  SERVER = "server",
}

export const MODAL_FUNCTION_TO_APP = {
  [ModalFunction.DELETE_MODEL]: ModalApp.SERVER,
  [ModalFunction.INFERENCE]: ModalApp.SERVER,
  [ModalFunction.TRAINING]: ModalApp.SERVER,
  [ModalFunction.UPLOAD_MODEL]: ModalApp.SERVER,
  [ModalFunction.YOUTUBE_TO_MP3]: ModalApp.SERVER,
  [ModalFunction.TEXT_TO_MUSIC]: ModalApp.SERVER,
  [ModalFunction.TEXT_TO_SONG]: ModalApp.SERVER,
  [ModalFunction.GENERATE_VIDEO]: ModalApp.SERVER,
  [ModalFunction.GENERATE_STEMS]: ModalApp.SERVER,
} as const;

const modalAxios = (functionName: ModalFunction, token?: string) => {
  const isDev = env.NEXT_PUBLIC_VERCEL_ENV === "development";
  const isStaging = env.NEXT_PUBLIC_VERCEL_ENV === "preview";

  const app = MODAL_FUNCTION_TO_APP[functionName];

  let url = "";

  if (isDev || isStaging) {
    url = `https://sovits-app-dev--${app}-${functionName}.modal.run/`;
  } else {
    url = `https://sovits-app-prod--${app}-${functionName}.modal.run/`;
  }

  // text-to-music / text-to-song return job-accepted immediately, but they run on the
  // heavy ML images, so a COLD start is paid before the handler begins. This used to be
  // 60s against a server timeout of exactly 60s — when that raced, the client gave up on
  // a request that had often already spawned the job, and the caller wrote the row
  // `failed` with no reason. 238 of 239 song failures had no recorded cause because of it.
  //
  // These two are the reverse of INFERENCE below: here the CLIENT must outlive the
  // server (240s vs 180s), because giving up first marks a generation failed that is
  // actually running. For blocking work the server must outlive the client instead.
  const timeout =
    functionName === ModalFunction.TEXT_TO_MUSIC ||
    functionName === ModalFunction.TEXT_TO_SONG
      ? 240_000
      : functionName === ModalFunction.INFERENCE
        ? 510_000
        : 6 * 60 * 1000 + 15000; // 6 minutes and 15 seconds

  // `token` is minted server-side by the tRPC procedure that created the row this call
  // acts on (see server/utils/modalToken.ts). Modal reads the caller's identity from it
  // instead of from `user_id` in the body, which anyone could set to anything.
  const baseAxios = axios.create({
    baseURL: url,
    timeout,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  return baseAxios;
};

export default modalAxios;
